import { Box } from "@mui/material";
import { CustomStyles, getStyles } from "src/styles/theme";
import defaultStyles, { StylesClasses } from "./styles";
import type { SvgIconComponent } from "@mui/icons-material";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import RemoveCircleOutlinedIcon from "@mui/icons-material/RemoveCircleOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import LibraryAddCheckOutlinedIcon from "@mui/icons-material/LibraryAddCheckOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";

type StatsCardProps = {
  title: string;
  value: number | string;
  variant?: "horizontal" | "vertical";
  icon?: string;
  area?: string;
  /** share-of-total %, from API (pill stays hidden until provided) */
  sharePct?: number | string;
  /** 0–100 fill for the bottom bar (bar stays hidden until provided) */
  barPct?: number;
  customStyles?: CustomStyles<StylesClasses>;
};

type AccentRule = {
  test: (title: string) => boolean;
  color: string;
  Icon: SvgIconComponent;
};

/* icon + accent color per workflow stage, matched on the card title */
const ACCENT_RULES: AccentRule[] = [
  {
    test: (t) => t.startsWith("ALL"),
    color: "#1F6E4E",
    Icon: LayersOutlinedIcon,
  },
  {
    test: (t) => t.includes("REJECT"),
    color: "#A6452F",
    Icon: HighlightOffOutlinedIcon,
  },
  {
    test: (t) => t.includes("WITHDRAW") || t.includes("DESELECT"),
    color: "#8A94A6",
    Icon: RemoveCircleOutlinedIcon,
  },
  {
    test: (t) => t.includes("QUERY"),
    color: "#C05B2E",
    Icon: HelpOutlineOutlinedIcon,
  },
  {
    test: (t) => t.includes("PENDING"),
    color: "#B8892B",
    Icon: PendingActionsOutlinedIcon,
  },
  {
    test: (t) => t.includes("VERIF"),
    color: "#2E7DA6",
    Icon: FindInPageOutlinedIcon,
  },
  {
    test: (t) =>
      t.includes("DONE") ||
      t.includes("RECEIVED") ||
      t.includes("APPROVED") ||
      t.includes("RELEASED"),
    color: "#1F8A5B",
    Icon: TaskAltOutlinedIcon,
  },
  {
    test: (t) => t.includes("SELECT") || t.includes("REGISTER"),
    color: "#2E7DA6",
    Icon: LibraryAddCheckOutlinedIcon,
  },
  {
    test: (t) => t.includes("AVAILABLE"),
    color: "#7A5EA8",
    Icon: Inventory2OutlinedIcon,
  },
];

const FALLBACK_ACCENT = { color: "#1F6E4E", Icon: InsertChartOutlinedIcon };

const getAccent = (title: string) => {
  const haystack = (title || "").toUpperCase();
  return ACCENT_RULES.find((rule) => rule.test(haystack)) ?? FALLBACK_ACCENT;
};

const fmt = (value: number | string) => {
  const num = Number(value);
  return Number.isFinite(num)
    ? num.toLocaleString("en-IN", { maximumFractionDigits: 2 })
    : String(value);
};

const StatsCard = ({
  customStyles,
  title,
  value,
  area,
  sharePct,
  barPct,
}: StatsCardProps) => {
  const styles = getStyles<StylesClasses>(defaultStyles, customStyles);
  const { color, Icon } = getAccent(title);
  // zero-count stages render "quiet" so the active pipeline stands out
  const isEmpty = !Number(value) && !Number(area);

  return (
    <Box
      {...styles("valueCard", {
        opacity: isEmpty ? 0.65 : 1,
        backgroundColor: `${color}0F`,
        borderColor: `${color}2E`,
        ":hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 20px ${color}30`,
          borderColor: `${color}66`,
        },
      })}
    >
      <Box {...styles("topRow")}>
        <Box {...styles("iconChip", { backgroundColor: `${color}18` })}>
          <Icon sx={{ fontSize: "18px", color }} />
        </Box>
        <Box {...styles("title")}>{title}</Box>
        {sharePct !== undefined && (
          <Box
            {...styles("sharePill", {
              color,
              backgroundColor: `${color}14`,
            })}
          >
            {sharePct}%
          </Box>
        )}
      </Box>

      <Box {...styles("metrics")}>
        <Box>
          <Box {...styles("bigValue")}>{fmt(value)}</Box>
          <Box {...styles("metricLabel")}>Number</Box>
        </Box>
        <Box>
          <Box {...styles("smallValue")}>{fmt(area ?? 0)}</Box>
          <Box {...styles("metricLabel")}>Area (Ha)</Box>
        </Box>
      </Box>

      {barPct !== undefined && (
        <Box {...styles("barTrack")}>
          <Box
            {...styles("barFill", {
              width: `${Math.min(Math.max(barPct, 0), 100)}%`,
              backgroundColor: color,
            })}
          />
        </Box>
      )}
    </Box>
  );
};

export default StatsCard;
